<template>
  <div class="pure-g">
    <div class="pure-u-md-1-5 pure-u-lg-1-4"></div>
    <div class="pure-u-1 pure-u-md-3-5 pure-u-lg-1-2">
      <div class="content box padding-30-reactive">
        <div class="pure-g" v-if="active">
          <div class="pure-u-4-5"><h3 class="responsive">{{step}}. Your account</h3></div>
          <div class="pure-u-1-5 text-right">
            <button v-if="active && canEdit" v-on:click="editOverride" class="link-lookalike">Close</button>
          </div>

          <div class="pure-u-1 spacing-20"></div>

          <SsoButtons v-if="showSsoButtons"/>
          <div v-if="loginOnly" class="pure-u-1">
            <p>A Tavour account with this email already exists. Please log in with your email and password.</p>
            <button type="button" class="link-lookalike" @click="loginOnly = false">Make a new account instead</button>
          </div>
          <div v-if="ssoNewUser" class="pure-u-1">
            <p>
              We did not find a Tavour account for {{ ssoEmail }}.
              If you already use the Tavour app, sign in the same way you do there.
            </p>
            <button type="button" class="link-lookalike" :disabled="isBusy" @click="useOtherSignIn">Use a different sign-in</button>
            <p>Or check your name, and we will make your Tavour account.</p>
          </div>

          <form class="pure-u-1 pure-form pure-form-stacked font-14-sm" @submit.prevent="submit">
            <fieldset>

              <template v-if="!loginOnly">
              <div class="pure-u-1 pure-u-sm-1-4 text-right-sm"><label for="fname">First name</label></div>
              <div class="pure-u-1 pure-u-sm-1-2"><input type="text" id="fname" class="pure-input-1"
                                                         v-model="user.fname">
                <ValidationErrors :errors="validationErrors.fname"/>
              </div>

              <div class="pure-u-1 pure-u-sm-1-4 text-right-sm"><label for="lname">Last name</label></div>
              <div class="pure-u-1 pure-u-sm-1-2 padding-bottom-10"><input type="text" id="lname" class="pure-input-1"
                                                         v-model="user.lname">
                <ValidationErrors :errors="validationErrors.lname"/>
              </div>
              </template>

              <template v-if="!isSso">
                <div class="pure-u-1 pure-u-sm-1-4 text-right-sm"><label for="email">Email</label></div>
                <div class="pure-u-1 pure-u-sm-1-2 padding-bottom-10"><input id="email" type="email" class="pure-input-1 margin-bottom-0"
                                                            v-model="user.email">
                  <ValidationErrors :errors="validationErrors.email"/>
                </div>

                <div class="pure-u-1 pure-u-sm-1-4 text-right-sm"><label for="password">Password</label></div>
                <div class="pure-u-1 pure-u-sm-1-2 "><input id="password" type="password"
                                                            class="pure-input-1 margin-bottom-0" v-model="user.password">
                  <ValidationErrors :errors="validationErrors.password"/>
                </div>

                <template v-if="!loginOnly">
                <div class="pure-u-1 pure-u-sm-1-4 text-right-sm"><label for="password_confirmation">Confirm
                  Password</label></div>
                <div class="pure-u-1 pure-u-sm-1-2 "><input id="password_confirmation" class="pure-input-1"
                                                            type="password" v-model="user.password_confirmation">
                  <ValidationErrors :errors="validationErrors.password_confirmation"/>
                </div>
                </template>
              </template>

              <div class="pure-u-1 pure-u-sm-1-4"></div>

              <div class="pure-u-1 pure-u-sm-1-2">
                <Loading v-if="loading || isBusy"/>
                <button class="pure-button pure-button-primary block padding-10" type="submit" :disabled="disabled">
                  {{buttonText}}
                </button>
                <div class="text-red" v-if="error">
                  {{ error }}
                </div>
              </div>
            </fieldset>
          </form>
        </div>
        <div v-else class="pure-g">
          <div class="pure-u-4-5"><h3 class="responsive">{{step}}. Your account</h3></div>

          <ul v-if="user.email" class="summary pure-u-1">
            <li>
              <div class="hidden-sm pure-u-sm-1-5 text-right">Name</div>
              <div class="pure-u-1 pure-u-sm-3-5 padding-left-10">{{ this.user.fname }} {{ this.user.lname }}</div>
            </li>
            <li>
              <div class="hidden-sm pure-u-sm-1-5 text-right">Email</div>
              <div class="pure-u-1 pure-u-sm-3-5 padding-left-10">{{ this.user.email }}</div>
            </li>
            <li v-if="!isSso">
              <div class="hidden-sm pure-u-sm-1-5 text-right">Password</div>
              <div class="pure-u-1 pure-u-sm-3-5 padding-left-10">************</div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import session from '../../lib/session';
  import Loading from '../helpers/Loading';
  import user from '../../lib/user';
  import store from '../../lib/store';
  import ValidationErrors from '../helpers/ValidationErrors';
  import SsoButtons from './SsoButtons';
  import * as sso from '../../lib/sso';

  export default {
    name: 'UserCreatorDirect',
    components: {
      ValidationErrors,
      Loading,
      SsoButtons,
    },
    props: ['active', 'step'],
    data() {
      return {
        ssoNewUser: store.get('ssoNewUser') || null,
        ssoEnabled: sso.isEnabled(),
        signedInWithSso: Boolean(store.get('idToken')),
        // Email and password only, after a Google / Apple email matched a password account.
        loginOnly: false,
        loading: false,
        isBusy: false,
        validationErrors: {},
        canEdit: false,
        error: '',
        emailError: false,
        defaultErrorMsg: 'Something went wrong, please try again',
        user: {
          email: '',
          fname: '',
          lname: '',
          password: '',
          password_confirmation: '',
          referer: `https://subscriptions.tavour.com/${store.get('brewery')}/`,
        },
      };
    },
    created() {
      if (this.ssoNewUser) {
        this.user.email = this.ssoNewUser.email;
        this.user.fname = this.ssoNewUser.fname;
        this.user.lname = this.ssoNewUser.lname;
      } else if (store.get('authToken')) {
        this.getUserInfo();
      }
    },
    watch: {
      active() {
        /* global ga */
        ga('send', 'pageview', `/${store.get('subscription')}/UserCreator`);

        if (store.get('authToken') && !this.ssoNewUser) {
          this.getUserInfo();
        }
      },
    },
    methods: {
      createUser() {
        const body = Object.assign({}, this.user);
        body.subscribe_by_location = false;

        user.create(body, (err, res) => {
          this.isBusy = false;
          if (err) {
            if (res && res.statusCode === 422) {
              if (res.body.errors.email) {
//                this.emailError = true;
                this.loginUser();
              } else {
                this.validationErrors = res.body.errors;
              }
            } else {
              this.error = this.defaultErrorMsg;
            }
          } else {
            sso.clearSession();
            store.set('authToken', res.body.user.auth_token);
            this.canEdit = true;
            this.$emit('success');
          }
        });
      },
      updateUser() {
        user.update(this.user, (err, res) => {
          this.isBusy = false;
          if (err) {
            this.parseErrors(res);
          } else {
            this.canEdit = true;
            this.editOverride();
          }
        });
      },
      loginUser() {
        this.error = '';
        this.isBusy = true;
        session.create(this.user.email, this.user.password, (err, res) => {
          this.isBusy = false;
          if (err) {
            if (res && res.statusCode === 422) {
              this.error = 'That email and password do not match.';
              if (this.ssoEnabled) this.error += ` ${sso.SSO_HINT}`;
            } else {
              this.error = 'Something went wrong, please try again.';
            }
          } else if (res.body.session.user_in_service_area) {
            sso.clearSession();
            store.set('authToken', res.body.session.user_token);
            this.canEdit = true;
            this.$emit('success');
          } else {
            this.error = 'Sorry, we don\'t currently deliver to your area.';
          }
        });
      },
      getUserInfo() {
        this.loading = true;
        user.all((err, res) => {
          this.loading = false;
          this.isBusy = false;
          if (err) {
            this.parseErrors(res);
          } else if (res.body.user && res.body.user.in_service_area === false) {
            // Email login refuses these members too (session user_in_service_area).
            this.error = 'Sorry, we don\'t currently deliver to your area.';
          } else if (res.body.user) {
            this.user.email = res.body.user.email;
            this.user.fname = res.body.user.fname;
            this.user.lname = res.body.user.lname;

            if (!this.canEdit) {
              this.canEdit = true;
              this.$emit('success');
            }
          }
        });
      },
      createSsoUser() {
        const body = {
          fname: this.user.fname,
          lname: this.user.lname,
          zip_code: store.get('zipCode'),
          referer: this.user.referer,
        };
        user.create_sso(body, (err, res) => {
          const created = !err;
          const conflict = (res && res.statusCode === 409) ? res.body.code : null;
          if (conflict === 'account_exists') {
            // An email account that the sign-in could not link to: show the
            // email form, so the member can log in with the password.
            const email = this.user.email;
            this.isBusy = false;
            this.useOtherSignIn();
            this.user.email = email;
            this.loginOnly = true;
            return;
          }
          if (!created && !conflict) {
            this.isBusy = false;
            this.parseErrors(res);
            return;
          }
          // New tokens carry the new account id (custom:tavour_id). If the refresh
          // fails we go on: the API then reads the id from Cognito instead.
          sso.refresh(() => {
            this.isBusy = false;
            this.ssoNewUser = null;
            store.set('ssoNewUser', null);
            if (created) {
              this.canEdit = true;
              this.$emit('success');
            } else {
              this.getUserInfo();
            }
          });
        });
      },
      // Drops the Google / Apple login, so the member can pick another one.
      useOtherSignIn() {
        if (this.isBusy) return;
        sso.clearSession();
        store.set('authToken', null);
        this.ssoNewUser = null;
        this.signedInWithSso = false;
        this.loginOnly = false;
        this.user.fname = '';
        this.user.lname = '';
        this.user.email = '';
      },
      submit() {
        if (this.isBusy) return;
        this.isBusy = true;
        this.error = '';
        if (this.ssoNewUser) {
          this.createSsoUser();
        } else if (this.loginOnly) {
          this.loginUser();
        } else if (this.signedInWithSso && !this.canEdit) {
          // Signed in with Google / Apple but the account did not load (or is
          // outside the delivery area): try loading it again, never the email form.
          this.getUserInfo();
        } else if (this.canEdit) {
          this.updateUser();
        } else {
          this.createUser();
        }
      },
      edit() {
        this.$emit('editView', 'UserCreatorDirect');
      },
      editOverride() {
        this.$emit('editDone');
      },
      parseErrors(res) {
        if (res && res.statusCode === 422) {
          this.validationErrors = res.body.errors;
        } else {
          this.error = this.defaultErrorMsg;
        }
      },
    },
    computed: {
      showSsoButtons() {
        return this.ssoEnabled && !this.canEdit && !this.ssoNewUser;
      },
      isSso() {
        return Boolean(this.ssoNewUser) || this.signedInWithSso;
      },
      ssoEmail() {
        return sso.displayEmail(this.user.email);
      },
      buttonText() {
        if (this.ssoNewUser) {
          return 'Create account and continue';
        }
        if (this.loginOnly) {
          return 'Log in and continue';
        }
        if (this.canEdit) {
          return 'Update and continue';
        }
        return 'Next, where to send your beer';
      },
      disabled() {
        if (this.ssoNewUser) {
          return this.isBusy || this.user.fname === '' || this.user.lname === '';
        }
        return this.isBusy;
      },
    },
  };
</script>

<style scoped>

  label {
    margin-top: 10px;
    margin-right: 10px;
  }

</style>



// WEBPACK FOOTER //
// UserCreatorDirect.vue?7e969cb4